import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import axiosClient from '~/apis/axiosClient';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React from 'react';
import FireAlarmAlert from '~/components/common/fire-alarm-alert/FireAlarmAlert';

dayjs.extend(utc);
dayjs.extend(timezone);

export function notify(type: string, message: string, data?: any) {
  let option = { toastId: 'bor-toast' };
  if (type === 'error') {
    return toast.error(message, option);
  }

  if (type === 'success') {
    return toast.success(message, option);
  }

  if (type === 'warning') {
    return toast.warning(message, option);
  }

  if (type === 'info') {
    return toast.info(message, option);
  }

  if (type === 'fire_alarm') {
    return toast(
      (toastProps) =>
        React.createElement(FireAlarmAlert, {
          data: data,
          closeToast: toastProps.closeToast,
        }),
      {
        ...option,
        className: 'p-0 w-[475px] h-[119px] border border-white bg-[#ED1002] rounded-[20px] relative',
        position: 'top-center',
        hideProgressBar: true,
        closeButton: false,
      }
    );
  }

  return toast(message, option);
}

export function handleApiError(e: unknown, isNotify: boolean = true) {
  if (isNotify) {
    catchError(e, (message) => {
      notify('error', message || 'Something went wrong.');
    });
  }
}

export function catchError(e: unknown, handleError: (errorMessage: string) => void) {
  if (typeof e === 'string') {
    handleError(e);
  } else if (e instanceof AxiosError) {
    handleError(e?.response?.data);
  } else if (e instanceof Error) {
    handleError(e.message);
  }
}

export function cast(value: any) {
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'number') return value ?? 0;

  return value ?? '';
}

export async function exportExcel(columns: any, data: any, fileName: string = 'Data', sheetName: string = 'Sheet1') {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  worksheet.columns = columns;
  const headerRow = worksheet.getRow(1);

  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF005F73' },
  };

  headerRow.font = {
    name: 'Open Sans',
    bold: true,
    color: { argb: 'FFFFFFFF' },
  };

  worksheet.addRows(data);

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, `${fileName}_${dayjs().format('YYYYMMDD')}.xlsx`);
}

export async function readExcel(file: File): Promise<any[]> {
  if (file?.name?.toLowerCase().endsWith('.xlsx')) return await readXlsx(file);

  if (file?.name?.toLowerCase().endsWith('.csv')) return await readCSV(file);

  return [];
}

export async function readXlsx(file: File): Promise<any[]> {
  const buffer: ArrayBuffer = await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e?.target?.result;
      if (!(result instanceof ArrayBuffer)) {
        return reject(new Error('File content is not an ArrayBuffer.'));
      }
      resolve(result);
    };

    reader.onerror = (e) => reject(new Error('Error'));

    reader.readAsArrayBuffer(file);
  });

  const workbook = new ExcelJS.Workbook();

  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];
  let header: string[] = [];
  const data: any[] = [];

  worksheet.eachRow({ includeEmpty: false }, (row: any, rowNumber: number) => {
    if (rowNumber === 1) {
      header = row.values;
      return;
    }

    const obj: any = {};

    for (let i = 0; i < header.length; i++) {
      if (!i) continue;
      obj[header[i]] = row.values[i];
    }

    data.push(obj);
  });

  return data;
}

export async function readCSV(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error('Lỗi khi phân tích CSV: ' + results.errors[0].message));
        }
        resolve(results.data);
      },
      error: (err) => {
        reject(err);
      },
    });
  });
}

export function getFileName(url: string): string {
  const urlArray = url.split('_');
  if (urlArray.length > 1) urlArray.shift();
  const fileName = urlArray.join('_');

  return fileName;
}

export async function downloadFile(url: string, name?: string) {
  try {
    const response = await axiosClient({
      url,
      method: 'GET',
      responseType: 'blob',
    });

    const href = URL.createObjectURL(response.data);

    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', name ?? getFileName(url));
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  } catch (e) {
    notify('error', 'Có lỗi xảy ra');
  }
}

export function tzDayjs(date: any) {
  if (!date) return dayjs();

  return dayjs(date);
}

export function clearInputFilter() {
  if (typeof document === 'undefined') return;

  const inputs = document.querySelectorAll('.table-container input');

  inputs.forEach((input: any) => {
    if (input?.value) input.value = '';
  });
}
